/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AuthState {
  isAuthenticated: boolean;
  username: string;
  password?: string;
  token?: string;
  apiKey?: string;
}

const initialState: AuthState = {
  isAuthenticated: false,
  username: '',
  password: undefined,
  token: undefined,
  apiKey: undefined,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<{ username: string; password?: string; token?: string; apiKey?: string }>) => {
      state.isAuthenticated = true;
      state.username = action.payload.username;
      state.password = action.payload.password;
      state.token = action.payload.token;
      state.apiKey = action.payload.apiKey;
      // 保存到 localStorage
      localStorage.setItem('auth', JSON.stringify({
        username: action.payload.username,
        password: action.payload.password,
        token: action.payload.token,
        apiKey: action.payload.apiKey,
      }));
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.username = '';
      state.password = undefined;
      state.token = undefined;
      state.apiKey = undefined;
      // 清除 localStorage
      localStorage.removeItem('auth');
    },
    restoreAuth: (state) => {
      // 从 localStorage 恢复登录状态
      const authData = localStorage.getItem('auth');
      if (authData) {
        try {
          const parsed = JSON.parse(authData);
          state.isAuthenticated = true;
          state.username = parsed.username || '';
          state.password = parsed.password;
          state.token = parsed.token;
          state.apiKey = parsed.apiKey;
        } catch (e) {
          // 如果解析失败，清除无效数据
          localStorage.removeItem('auth');
        }
      }
    },
    // 更新 apiKey（用于开启新会话时更新）
    updateApiKey: (state, action: PayloadAction<string>) => {
      state.apiKey = action.payload;
      // 同步更新 localStorage
      const authData = localStorage.getItem('auth');
      if (authData) {
        try {
          const parsed = JSON.parse(authData);
          parsed.apiKey = action.payload;
          localStorage.setItem('auth', JSON.stringify(parsed));
        } catch (e) {
          // ignore
        }
      }
    },
  },
});

export const { login, logout, restoreAuth, updateApiKey } = authSlice.actions;

export default authSlice.reducer;
